export interface TocHeading {
  id: string;
  text: string;
  level: number;
}

const FENCE = /^\s{0,3}(```|~~~)/;

/** Heading text as rehype-slug sees it: it slugs the RENDERED text, so link, code and emphasis markers are gone. */
function plainHeading(md: string): string {
  return md
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/<[^>]+>/g, "")
    .replace(/[`*~]/g, "")
    .trim();
}

/**
 * github-slugger's rule (what rehype-slug uses): lower-case, drop punctuation and symbols, spaces become hyphens.
 * Each dropped character leaves nothing behind, so "Tips & Best Practices" is "tips--best-practices".
 */
function slugify(text: string): string {
  return text.toLowerCase().replace(/[^\p{L}\p{M}\p{N}\p{Pc} -]/gu, "").replace(/ /g, "-");
}

/**
 * The h2/h3 headings of a docs page with the ids rehype-slug gives them, duplicates numbered -1, -2 ... exactly as
 * github-slugger does (one slugger per document, every heading level counts, and a generated id is itself taken).
 */
export function extractHeadings(markdown: string): TocHeading[] {
  const out: TocHeading[] = [];
  const occurrences = new Map<string, number>();
  let fenced = false;
  for (const line of markdown.split("\n")) {
    if (FENCE.test(line)) {
      fenced = !fenced;
      continue;
    }
    if (fenced) continue;
    const m = line.match(/^(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (!m) continue;
    const text = plainHeading(m[2]);
    const base = slugify(text);
    let id = base;
    while (occurrences.has(id)) {
      occurrences.set(base, (occurrences.get(base) ?? 0) + 1);
      id = `${base}-${occurrences.get(base)}`;
    }
    occurrences.set(id, 0);
    const level = m[1].length;
    if (level === 2 || level === 3) out.push({ id, text, level });
  }
  return out;
}
